'use client';

import {
  Badge,
  Card,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@routly/ui';
import { ChevronDown, ChevronRight, ScrollText } from 'lucide-react';
import { Fragment, useState } from 'react';
import { EmptyState, ErrorState, LoadingState, PageHeader, Pagination } from '@/components/shared';
import { useAudit, useAuditActions } from '@/hooks/useAudit';
import type { AuditEntry } from '@/lib/types';

const ANY = 'any';
const PAGE_SIZE = 50;

/**
 * Who changed what, and when.
 *
 * The audit module declared nineteen actions and wrote them to the structured
 * log; seven were ever emitted and none was stored anywhere a person could
 * query. A customer asking who changed a driver's role last quarter had nowhere
 * to look.
 */
export function AuditPage() {
  const [page, setPage] = useState(1);
  const [action, setAction] = useState(ANY);
  const [result, setResult] = useState(ANY);
  const [expanded, setExpanded] = useState<string | null>(null);

  const { data, isLoading, error } = useAudit({
    page,
    limit: PAGE_SIZE,
    action: action === ANY ? undefined : action,
    result: result === ANY ? undefined : (result as 'success' | 'failure'),
  });
  const { data: actions = [] } = useAuditActions();

  const entries = data?.data ?? [];
  const isFiltered = action !== ANY || result !== ANY;

  const changeFilter = (setter: (value: string) => void) => (value: string) => {
    setter(value);
    setPage(1);
  };

  return (
    <>
      <PageHeader title="Audit trail" description={`${data?.pagination?.total ?? 0} entries`} />

      <div className="flex flex-wrap gap-3">
        <Select value={action} onValueChange={changeFilter(setAction)}>
          <SelectTrigger className="w-72" aria-label="Action">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY}>All actions</SelectItem>
            {actions.map((name) => (
              <SelectItem key={name} value={name}>
                {name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={result} onValueChange={changeFilter(setResult)}>
          <SelectTrigger className="w-48" aria-label="Result">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY}>Any result</SelectItem>
            <SelectItem value="success">Success</SelectItem>
            <SelectItem value="failure">Failed</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <LoadingState />
      ) : error ? (
        <ErrorState title="Could not load the audit trail" message={error.message} />
      ) : entries.length === 0 ? (
        <EmptyState
          icon={ScrollText}
          title={isFiltered ? 'Nothing found' : 'Nothing recorded yet'}
          description={
            isFiltered
              ? 'Try a wider filter.'
              : 'Actions appear here as soon as somebody changes something.'
          }
        />
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8" />
                <TableHead>When</TableHead>
                <TableHead>Who</TableHead>
                <TableHead>Action</TableHead>
                <TableHead>On what</TableHead>
                <TableHead>Result</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {entries.map((entry) => (
                <AuditRow
                  key={entry.id}
                  entry={entry}
                  isOpen={expanded === entry.id}
                  onToggle={() =>
                    setExpanded((current) => (current === entry.id ? null : entry.id))
                  }
                />
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {data?.pagination && data.pagination.totalPages > 1 ? (
        <Pagination
          pagination={data.pagination}
          currentPage={page}
          onPageChange={setPage}
          itemName="entries"
        />
      ) : null}
    </>
  );
}

function AuditRow({
  entry,
  isOpen,
  onToggle,
}: {
  entry: AuditEntry;
  isOpen: boolean;
  onToggle: () => void;
}) {
  const hasChanges = Boolean(entry.changes?.before || entry.changes?.after);

  return (
    <Fragment>
      <TableRow>
        <TableCell>
          {hasChanges ? (
            <button
              type="button"
              onClick={onToggle}
              aria-expanded={isOpen}
              aria-label={isOpen ? 'Hide changes' : 'Show changes'}
              className="text-muted-foreground hover:text-foreground"
            >
              {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </button>
          ) : null}
        </TableCell>
        <TableCell className="whitespace-nowrap text-sm text-muted-foreground tabular-nums">
          {new Date(entry.createdAt).toLocaleString()}
        </TableCell>
        <TableCell className="text-sm">
          <div className="text-foreground">{entry.actorEmail ?? 'not identified'}</div>
          {entry.actorIp ? (
            <div className="text-xs text-muted-foreground tabular-nums">{entry.actorIp}</div>
          ) : null}
        </TableCell>
        <TableCell>
          <code className="text-xs text-foreground">{entry.action}</code>
        </TableCell>
        <TableCell className="text-sm text-muted-foreground">
          {entry.targetName ?? entry.targetId ?? '—'}
          {entry.targetType ? (
            <span className="ml-1 text-xs opacity-70">({entry.targetType})</span>
          ) : null}
        </TableCell>
        <TableCell>
          <Badge variant={entry.result === 'success' ? 'success' : 'destructive'}>
            {entry.result === 'success' ? 'Success' : 'Failed'}
          </Badge>
          {entry.reason ? (
            <div className="mt-1 text-xs text-muted-foreground">{entry.reason}</div>
          ) : null}
        </TableCell>
      </TableRow>

      {isOpen && hasChanges ? (
        <TableRow>
          <TableCell colSpan={6} className="bg-secondary/40">
            {/*
              Rendered as JSON on purpose: the shapes differ per action, and a
              pretty renderer per action is how the honest record starts
              disagreeing with what is displayed.
            */}
            <div className="grid gap-4 md:grid-cols-2">
              <ChangeBlock label="Before" value={entry.changes?.before} />
              <ChangeBlock label="After" value={entry.changes?.after} />
            </div>
          </TableCell>
        </TableRow>
      ) : null}
    </Fragment>
  );
}

function ChangeBlock({ label, value }: { label: string; value?: Record<string, unknown> }) {
  if (!value) return null;
  return (
    <div>
      <p className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </p>
      <pre className="overflow-x-auto rounded-md border border-border bg-background p-2 text-xs text-foreground">
        {JSON.stringify(value, null, 2)}
      </pre>
    </div>
  );
}
